import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid, GridColDef, GridRowId, GridPagination, GridSortModel } from '@mui/x-data-grid';
import { Button, IconButton, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, InputAdornment, Menu, MenuItem, useMediaQuery } from '@mui/material';
import { MobileNumberField } from './MobileNumberField';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { NumberInputCell } from './NumberInputCell';
import { NumberInput } from './NumberInput';
import { HEADER_FOOTER_HEIGHT, ROW_HEIGHT } from '../utils/constants';
import { colors } from '../theme';
import {
  MarketRentAssumption,
  layoutNameTaken,
  parseSqFt,
  planSquareFeet,
  suggestLayoutName
} from '../utils/floorPlans';
import { isDuplicateRentalName, renameRentalGrowthRate } from '../utils/growthRates';




interface Unit {
  id: string;
  rent_type: string;
  vacate_flag: number;
  layout: string;
  square_feet: number;
  vacate_month: number;
  current_rent: number | null;
  // Populated by a rent-roll import; null everywhere else. Must stay identical
  // to the Unit shapes in CreateModel / UnitMixTable.
  unit_label?: string | null;
  lease_end?: string | null;
  market_rent_override?: number | null;
  is_section8?: boolean | null;
  import_source?: string | null;
  unitNumber?: number | string;
  isFooter?: boolean;
}

interface UnitTableProps {
  units: Unit[];
  setUnits: React.Dispatch<React.SetStateAction<Unit[]>>;
  showMarketRentLayouts: boolean;
  showMarketRentAssumptions: boolean;
  marketRentAssumptions: MarketRentAssumption[];
  setMarketRentAssumptions: React.Dispatch<React.SetStateAction<MarketRentAssumption[]>>;
  showGrowthRates: boolean;
  growthRates: { name: string; value: number; type: string; }[];
  setGrowthRates: React.Dispatch<React.SetStateAction<{ name: string; value: number; type: string; }[]>>;
  modelDetails: any;
  highlightedFields: string[];
  growthRatesOnly: boolean;
  vacate: boolean;
  activeStepTitle: string;
  allowAddUnit?: boolean;
}

interface SelectedCell {
  rowIdx: number;
  columnKey: string;
}

// Rent-change behaviour, in vacate_flag order. The mobile card view reads the
// labels straight off this list.
const VACATE_OPTIONS = [
  { value: 0, label: 'Keep Tenant' },
  { value: 1, label: 'Vacate & Re-Lease' },
  { value: 2, label: 'Market Adjustment' },
];

// Columns offering a "set for all units" header control.
type SetAllField = 'vacate_flag' | 'rent_type';

// Sort comparator for the grid. Blanks sort first, numbers compare
// numerically, everything else compares as text with embedded numbers handled
// naturally ("Unit 2" before "Unit 10").
const compareValues = (a: any, b: any) => {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return -1;
  if (bEmpty) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const rowHeight = 52; // Example row height, adjust as needed
const headerFooterHeight = 56 + 37; // Example header height, adjust as needed

// Custom dropdown component for rent_type
const RentTypeDropdown: React.FC<{ value: string; onChange: (value: string) => void; options: { name: string; }[]; }> = ({ value, onChange, options }) => {
  return (
    <div className="u-select-wrap" style={{ position: 'relative', width: '100%' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
            e.stopPropagation();
          }
        }}
        style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: 4, padding: '10px 28px 10px 8px', outline: 'none', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
        className="u-select"
      >
        {options.map((option) => (
          <option key={option.name} value={option.name}>
            {option.name}
          </option>
        ))}
      </select>
      <span className="u-caret" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>▾</span>
    </div>
  );
};

const UnitTable: React.FC<UnitTableProps> = ({ units, setUnits,  marketRentAssumptions, showMarketRentAssumptions, showMarketRentLayouts, setMarketRentAssumptions, showGrowthRates, setGrowthRates, growthRates,modelDetails, highlightedFields, growthRatesOnly, vacate, activeStepTitle, allowAddUnit = true }) => {
  const [unitCount, setUnitCount] = useState(units.length || 1);
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  const [isPasteOperation, setIsPasteOperation] = useState(false);
  const [pastedText, setPastedText] = useState<string>('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const [setAllMenu, setSetAllMenu] = useState<{ anchor: HTMLElement; field: SetAllField } | null>(null);
  const [setAllConfirm, setSetAllConfirm] = useState<
    { field: SetAllField; value: number | string; label: string } | null
  >(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  // Row order captured when a column header is clicked. Null means natural
  // (entry) order.
  const [rowOrder, setRowOrder] = useState<string[] | null>(null);
  const [open, setOpen] = useState(false);
  const [newLayoutName, setNewLayoutName] = useState('');
  const [newLayoutSqFt, setNewLayoutSqFt] = useState('');
  const [layoutSqFtEdit, setLayoutSqFtEdit] = useState<{ [index: number]: string }>({});
  const [sqFtCascade, setSqFtCascade] = useState<
    { layout: string; nextSqFt: number; prevSqFt: number | null; count: number; skipped: number } | null
  >(null);

  const [growthOpen, setGrowthOpen] = useState(false);
  const [newGrowthRateName, setNewGrowthRateName] = useState('');
  const [nameEdit, setNameEdit] = useState<{ [index: number]: string }>({});
  const [nameError, setNameError] = useState<{ [index: number]: string }>({});
  const [pfOpen, setPfOpen] = useState(showMarketRentAssumptions && growthRatesOnly === false);


  let idCounter = 0;

  const generateUniqueId = () => {
    const timestamp = Date.now();
    idCounter += 1;
    return `${timestamp}-${idCounter}`;
  };

  // Square feet defined on a floor plan; 0 when the plan has none (legacy models).
  const layoutSquareFeet = (layout: string) =>
    planSquareFeet(marketRentAssumptions.find((a) => a.layout === layout));

  useEffect(() => {
    if (units.length === 0 && growthRates.length > 0 && marketRentAssumptions.length > 0) {
      const firstPlan = marketRentAssumptions[0];
      const firstPlanSqFt = Number(firstPlan.square_feet ?? 0);
      setUnits([
        {
          id: generateUniqueId(),
          rent_type: growthRates[0].name,
          vacate_flag: 0,
          layout: firstPlan.layout,
          square_feet: firstPlanSqFt > 0 ? firstPlanSqFt : 0,
          vacate_month: 0,
          current_rent: null
        }
      ]);
    }
  }, [units, setUnits, growthRates]);

  const createNewUnit = (): Unit => {
    const layout = marketRentAssumptions[0]?.layout || 'Studio';
    return {
      id: generateUniqueId(),
      rent_type: (growthRates.find(r => r.type === 'rental')?.name) || growthRates[0]?.name || 'Fair Market',
      vacate_flag: 0,
      layout,
      square_feet: layoutSquareFeet(layout),
      vacate_month: 0,
      current_rent: null,
    };
  };

  const addSingleUnit = () => {
    setUnits((prev) => [...prev, createNewUnit()]);
  };

  // Example: fieldsToShow could come from props, context, or be computed
  const fieldsToShow = [
    'unitNumber',
    'layout',
    'square_feet',
    'current_rent',
    'rent_type',
    ...(activeStepTitle === "Residential Rental Units" ? ['growth_rate'] : []),
    'pf_rent', // only if showMarketRentAssumptions
    'vacate_flag',
    'vacate_month',
 
    'delete'
  ];

  // const NumberInputCell = ({ params, handleCellChange, field }: { params: any, handleCellChange: any, field: string }) => {
  //   const inputRef = useRef<HTMLInputElement>(null);

  //   useEffect(() => {
  //     if (params.hasFocus && inputRef.current) {
  //       inputRef.current.focus();
  //       inputRef.current.select();
  //     }
  //   }, [params.hasFocus]);

  //   return (
  //     <input
  //       ref={inputRef}
  //       type="number"
  //       min={0}
  //       value={params.row[field] ?? ''}
  //       onChange={e => handleCellChange(params.id, field,  Number(e.target.value))}
  //       style={{
  //         width: '96%',
  //         border: params.hasFocus ? '2px solid #1976d2' : '1px solid #ccc',
  //         background: params.hasFocus ? '#e3f2fd' : 'transparent',
  //         padding: '10px 6px',
  //         outline: 'none',
  //         borderRadius: 4,
  //         fontSize: 14, 
  //         textAlign: 'right'
  //       }}
  //       onFocus={e => e.target.select()}
  //     />
  //   );
  // };  

  // --- "Set for all units" header controls ---------------------------------
  // Bulk-setting a column beats walking 300 dropdowns. Per-unit dropdowns still
  // override individually afterwards; this just seeds them all at once.
  const setAllOptions = (field: SetAllField): { value: number | string; label: string }[] =>
    field === 'vacate_flag'
      ? VACATE_OPTIONS
      : growthRates
          .filter((rate) => rate.type === 'rental')
          .map((rate) => ({ value: rate.name, label: rate.name }));

  const renderSetAllButton = (field: SetAllField) => (
    <Tooltip title="Set for all units" arrow>
      <span>
        <IconButton
          size="small"
          disabled={units.length === 0}
          // Without this the click also toggles the column's sort.
          onClick={(e) => {
            e.stopPropagation();
            setSetAllMenu({ anchor: e.currentTarget, field });
          }}
          sx={{ p: 0.25 }}
        >
          <PlaylistAddCheckIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  const applySetAll = () => {
    if (!setAllConfirm) return;
    const { field, value } = setAllConfirm;
    setUnits((prevUnits) => prevUnits.map((unit) => ({ ...unit, [field]: value })));
    setSetAllConfirm(null);
  };

  const allColumns: Record<string, GridColDef> = {
    unitNumber: { 
      field: 'unitNumber', 
      headerName: 'Unit', 
      width: 70, 
      editable: false,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 14.5, color: '#1f2937' }}>Unit</Typography>
          {allowAddUnit && (
            <Tooltip title="Add unit" arrow>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); addSingleUnit(); }}
                sx={{ p: 0.25 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ color: '#9e9e9e'}}>{params.value}</span>
          <span className="u-row-action">
            <Tooltip title="Duplicate" arrow>
              <IconButton
                onClick={() => handleDuplicateRow(params.id)}
                color="primary"
                size="small"
                sx={{ p: 0.25 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </span>
        </Box>
      )
    },
    layout: {
      field: 'layout',
      headerName: 'Layout',
      width: 150,
      editable: false,
      cellClassName: showMarketRentAssumptions ? 'u-noneditable-cell' : 'u-editable-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 14.5, color: '#1f2937' }}>Layout</Typography>
          <Tooltip title="Edit layouts" arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleOpenModal(); }}
              sx={{ p: 0.25 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => (
        showMarketRentAssumptions ? (
          <span className="u-muted">{params.value}</span>
        ) : (
          <div className="u-select-wrap" style={{ position: 'relative', width: '100%' }}>
            <select
              value={params.value}
              onChange={(e) => handleCellChange(params.id, 'layout', e.target.value)}
              onKeyDown={(e) => {
                if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                  e.stopPropagation();
                }
              }}
              style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: 4, padding: '10px 28px 10px 8px', outline: 'none', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
              className="u-select"
            >
              {marketRentAssumptions.map((assumption) => (
                <option key={assumption.layout} value={assumption.layout}>
                  {assumption.layout}
                </option>
              ))}
            </select>
            <span className="u-caret" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>▾</span>
          </div>
        )
      )
    },
    square_feet: { 
      field: 'square_feet', 
      headerName: 'Square Feet', 
      width: 150, 
      minWidth: 100,
      editable: false, 
      type: 'number',
      cellClassName: showMarketRentAssumptions ? 'u-noneditable-cell' : 'u-editable-cell',
      renderCell: (params) => (
        showMarketRentAssumptions ? (
          <span>
            <span className="u-value u-muted">{Number(params.value ?? 0).toLocaleString()}</span>{' '}
            <span className="u-unit u-muted">sf</span>
          </span>
        ) : (
          <NumberInputCell
            params={params}
            handleCellChange={handleCellChange}
            field={'square_feet'}
            highlighted={highlightedFields?.includes('square_feet')}
            suffix={'sf'}
          />
        )
      ),
    },
    
    current_rent: { field: 'current_rent', headerName: 'Current Rent', width: 160, minWidth: 150, editable: false, type: 'number',
      cellClassName: showMarketRentAssumptions ? 'u-noneditable-cell' : 'u-editable-cell',
      renderCell: (params) => (
        showMarketRentAssumptions ? (
          <span>
            <span className="u-value u-muted">$ {Number(params.value ?? 0).toLocaleString()}</span>{' '}
            <span className="u-unit u-muted">/ mo</span>
          </span>
        ) : (
          <NumberInputCell
            params={params}
            handleCellChange={handleCellChange}
            field={'current_rent'}
            highlighted={highlightedFields?.includes('current_rent')}
            prefix={'$'}
            suffix={'/ mo'}
          />
        )
      ),
     },
    rent_type: {
      field: 'rent_type',
      headerName: 'Rent Type',
      width: 150,
      editable: false,
      cellClassName: showMarketRentAssumptions ? 'u-noneditable-cell' : 'u-editable-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 14.5, color: '#1f2937' }}>Rent Type</Typography>
          <Tooltip title="Edit rent types & growth" arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleOpenGrowthModal(); }}
              sx={{ p: 0.25 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* Only where the per-unit dropdown is editable — elsewhere rent type
              is read-only and driven by the market rent assumptions. */}
          {!showMarketRentAssumptions && renderSetAllButton('rent_type')}
        </Box>
      ),
      renderCell: (params) => (
        showMarketRentAssumptions ? (
          <span className="u-muted">{params.value}</span>
        ) : (
          <RentTypeDropdown
            value={params.value}
            onChange={(newValue) => handleCellChange(params.id, 'rent_type', newValue)}
            options={growthRates.filter(rate => rate.type === 'rental')}
          />
        )
      )
    },
    
    ...(activeStepTitle === "Residential Rental Units"
      ? {
          growth_rate: {
            field: 'growth_rate',
            headerName: 'Growth Rate',
            width: 120,
            editable: false,
            cellClassName: 'u-noneditable-cell',
            renderHeader: () => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 14.5, color: '#1f2937' }}>Growth %</Typography>
                <Tooltip title="Edit rent types & growth" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleOpenGrowthModal(); }}
                    sx={{ p: 0.25 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ),
            renderCell: (params) => (
              <span>
                <span className="u-value">{Number(params.value ?? 0).toLocaleString()}</span>
                <span className="u-unit">%</span>
              </span>
            )
          }
        }
      : {}),

    vacate_flag: {
      field: 'vacate_flag',
      headerName: 'Vacate?',
      width: 200,
      editable: false,
      cellClassName: 'u-editable-cell',
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 14.5, color: '#1f2937' }}>Vacate?</Typography>
          {renderSetAllButton('vacate_flag')}
        </Box>
      ),
      renderCell: (params) => {
        const v = params.value !== undefined && params.value !== null ? Number(params.value) : null;
        const tooltipText =
          v === 0
            ? "Keep Tenant: Tenant stays, rent follows annual growth assumptions."
            : v === 1
            ? "Vacate & Re-Lease: Tenant leaves, unit goes vacant, then re-leases at market rents."
            : v === 2
            ? "Market Adjustment: Tenant remains, rent immediately adjusts to market this year."
            : "Select rent change behavior";
        return (
          <Tooltip title={tooltipText} arrow placement="left">
            <Box component="span" sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <select
          value={params.value !== undefined && params.value !== null ? params.value : ''}
          onChange={(e) => handleCellChange(params.id, 'vacate_flag', parseInt(e.target.value, 10))}
          onKeyDown={(e) => {
            if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
              e.stopPropagation();
            }
          }}
          style={{ width: '100%', background: 'transparent', border: 'none', borderRadius: 4, padding: '8px 28px 8px 8px', outline: 'none', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', color: 'inherit', fontSize: 14.5, fontWeight: 500, fontFamily: 'inherit', textAlign: 'center', textAlignLast: 'center' }}
          className="u-select"
        >
                <option value={0}>Keep Tenant</option>
                <option value={1}>Vacate & Re-Lease</option>
                <option value={2}>Market Adjustment</option>
        </select>
        <span className="u-caret" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>▾</span>
            </Box>
          </Tooltip>
        );
      },
      type: 'number'
    },
    vacate_month: { field: 'vacate_month', headerName: 'Vacate Month', width: 120, editable: false, type: 'number',
      cellClassName: 'u-editable-cell',
      renderCell: (params) => <NumberInputCell prefix={'Month '} params={params} handleCellChange={handleCellChange} field={'vacate_month'} />,
     },
     pf_rent: {
      field: 'pf_rent',
      headerName: 'Pro Forma Rent',
      width: 200,
      editable: false,
      cellClassName: 'u-noneditable-cell',
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 14.5, color: '#1f2937' }}>Pro Forma Rent</Typography>
          <Tooltip title="From market rent assumptions by layout (read-only)" arrow>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
            </span>
          </Tooltip>
        </Box>
      ),
      type: 'number',
      renderCell: (params) => (
        <span>
          <span className="u-value">$ {Number(params.value ?? 0).toLocaleString()}</span>{' '}
          <span className="u-unit">/ mo</span>
        </span>
      ),
    },
     delete: {
      field: 'delete',
      headerName: '',
      width: 100,
      renderCell: (params) => (
        <span className="u-row-action">
          <Tooltip title="Delete">
            <span>
              <IconButton
                onClick={() => handleDeleteRow(params.id)}
                color="error"
                disabled={units.length === 1}
                size="small"
                sx={{
                  color: units.length === 1 ? 'gray' : 'error.main',
                  cursor: units.length === 1 ? 'not-allowed' : 'pointer',
                }}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </span>
      )
    }
  };

  // Build columns dynamically and make them flex to fill 100% width
  const flexByField: Record<string, number> = {
    unitNumber: 0.6,
    layout: 0.9,
    square_feet: 0.9,
    current_rent: 1,
    rent_type: 1,
    growth_rate: 0.8,
    pf_rent: 1.3,
    vacate_flag: 1.3,
    vacate_month: 0.9,
    delete: 0.3,
  };

  // Narrowest each column can go before its header or values clip. The grid's
  // own minimum width is the sum of these, so it only scrolls sideways when the
  // columns genuinely cannot fit (a landscape tablet fits all of them).
  const minWidthByField: Record<string, number> = {
    unitNumber: 56,
    layout: 90,
    square_feet: 92,
    current_rent: 108,
    rent_type: 124,
    growth_rate: 100,
    pf_rent: 140,
    vacate_flag: 160,
    vacate_month: 104,
    delete: 44,
  };

  const columns: GridColDef[] = fieldsToShow
    .filter((field) => field !== 'pf_rent' || showMarketRentAssumptions)
    // .filter((field) => field !== 'rent_type' || showGrowthRates)
    .map((field) => {
      const col = { ...allColumns[field] } as GridColDef;
      // Ensure flexible columns that fill the grid width
      delete (col as any).width;
      col.flex = flexByField[field] ?? 1;
      col.minWidth = minWidthByField[field] ?? 70;
      if (highlightedFields && highlightedFields.includes(field)) {
        col.headerClassName = 'u-highlight';
        col.cellClassName = 'u-highlight';
      }
      return col;
    });

  const hiddenWhenNotVacate = ['vacate_flag', 'vacate_month'];
  const gridMinWidth = columns
    .filter((col) => vacate || !hiddenWhenNotVacate.includes(col.field))
    .reduce((sum, col) => sum + (col.minWidth ?? 0), 0);

  const handleCellChange = (id: GridRowId, field: string, value: any) => {
    setUnits((prevUnits) =>
      prevUnits.map((unit) => {
        if (unit.id !== id) return unit;
        // Picking a layout seeds the unit's square feet from the floor plan,
        // but never overwrites a square footage the user already entered.
        if (field === 'layout') {
          const currentSqFt = Number(unit.square_feet ?? 0);
          const fromPlan = layoutSquareFeet(value);
          if (fromPlan > 0 && (!currentSqFt || currentSqFt <= 0)) {
            return { ...unit, layout: value, square_feet: fromPlan };
          }
        }
        return { ...unit, [field]: value };
      })
    );
  };

  const handleCellCopy = (event: ClipboardEvent) => {
    event.preventDefault();
    if (selectedCell) {
      const { rowIdx, columnKey } = selectedCell;
      const value = units[rowIdx][columnKey as keyof Unit];

      event.clipboardData?.setData('text/plain', String(value));
    }
  };

  const handlePaste = (event: ClipboardEvent) => {
    setIsPasteOperation(true);
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedData = clipboardData.getData('Text');
    setPastedText(pastedData);
  };

  // Add event listeners for copy and paste events
  useEffect(() => {
    document.addEventListener('copy', handleCellCopy);
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('copy', handleCellCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [selectedCell]);

  const updateUnits = (count: number) => {

    const newUnits: Unit[] = Array.from({ length: count }, () => ({
      id: generateUniqueId(),
      rent_type: growthRates[0].name,
      vacate_flag: 0,
      layout: marketRentAssumptions[0].layout,
      square_feet: layoutSquareFeet(marketRentAssumptions[0].layout),
      vacate_month: 0,
      current_rent: null
    }));
    setUnits([...units, ...newUnits]);
  };

  const processRowUpdate = (newRow: Unit) => {


    newRow.vacate_month = Math.max(0, Math.round(Number(newRow.vacate_month)));
    newRow.square_feet = Math.max(0, newRow.square_feet);
    newRow.current_rent = Math.max(0, newRow.current_rent || 0);
    
    setUnits((prevUnits) =>
      prevUnits.map((unit) =>
        unit.id === newRow.id ? newRow : unit
      )
    );
    return newRow;
  };

  const handleDeleteRow = (id: GridRowId) => {
    // Drop focus first so the grid doesn't query a soon-deleted cell
    try {
      if (typeof window !== 'undefined' && (document as any)?.activeElement) {
        (document as any).activeElement?.blur?.();
      }
    } catch {}
    // Defer actual removal until the next frame to let MUI finish the event cycle
    requestAnimationFrame(() => {
    setUnits((prevUnits) => prevUnits.filter((unit) => unit.id !== id));
    });
  };

  const handleDuplicateRow = (id: GridRowId) => {
    const unitToDuplicate = units.find((unit) => unit.id === id);
    if (unitToDuplicate) {
      const newUnit = { ...unitToDuplicate, id: generateUniqueId() };
      setUnits((prevUnits) => [...prevUnits, newUnit]);
    }
  };

  // Update rows to include unitNumber and pf_rent. Lookups are keyed once per
  // change rather than scanning the assumption and growth-rate arrays for every
  // row, which is what made a 300-unit rent roll crawl. Both maps keep
  // first-match-wins, matching the .find() calls they replace.
  const rows = useMemo(() => {
    const pfRentByLayout = new Map<string, number>();
    marketRentAssumptions.forEach((assumption) => {
      if (!pfRentByLayout.has(assumption.layout)) {
        pfRentByLayout.set(assumption.layout, assumption.pf_rent ?? 0);
      }
    });

    const growthByRentType = new Map<string, number>();
    growthRates.forEach((rate) => {
      if (rate.type !== 'rental') return;
      const key = (rate.name ?? '').toString().trim().toLowerCase();
      if (!growthByRentType.has(key)) growthByRentType.set(key, rate.value ?? 0);
    });

    // Unit numbers come from the unit's place in the rent roll, not its place
    // on screen, so a sorted view still identifies each row the same way.
    const baseRows = units.map((unit, index) => ({
      ...unit,
      unitNumber: index + 1,
      pf_rent: pfRentByLayout.get(unit.layout) ?? 0,
      growth_rate: growthByRentType.get((unit.rent_type ?? '').toString().trim().toLowerCase()) ?? 0
    }));

    if (!rowOrder) return baseRows;

    const position = new Map<string, number>();
    rowOrder.forEach((id, i) => position.set(id, i));
    // Units added since the sort (duplicated, or produced by the generator)
    // have no captured position and stay at the end.
    return baseRows.slice().sort((a, b) => {
      const pa = position.has(a.id) ? (position.get(a.id) as number) : Number.MAX_SAFE_INTEGER;
      const pb = position.has(b.id) ? (position.get(b.id) as number) : Number.MAX_SAFE_INTEGER;
      return pa - pb;
    });
  }, [units, marketRentAssumptions, growthRates, rowOrder]);

  // Sorting reorders the rows once, on the click, rather than live-sorting on
  // every change: a grid that re-sorts while you type in a cell yanks the row
  // out from under the cursor.
  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    if (!model.length || !model[0].sort) {
      setRowOrder(null);
      return;
    }
    const { field, sort } = model[0];
    const direction = sort === 'desc' ? -1 : 1;
    const ordered = rows
      .slice()
      .sort((a: any, b: any) => direction * compareValues(a[field], b[field]));
    setRowOrder(ordered.map((row) => row.id));
  };

  const getRowClassName = (params: any) =>
    params.indexRelativeToCurrentPage % 2 === 0 ? 'u-row-odd' : 'u-row-even';

  // The grid needs a bounded height for MUI's row virtualization to kick in
  // (autoHeight renders every row on the page). Small rent rolls still size to
  // their content; large ones scroll inside the cap. The chrome allowance
  // covers the column header plus CustomFooter and is deliberately generous —
  // spare pixels show as a sliver of white, too few force a scrollbar onto a
  // grid that would otherwise fit.
  const MAX_GRID_HEIGHT = 640;
  const GRID_CHROME_HEIGHT = 120;
  const rowsOnPage = Math.max(1, Math.min(rows.length, paginationModel.pageSize));
  const gridHeight = Math.min(MAX_GRID_HEIGHT, GRID_CHROME_HEIGHT + rowsOnPage * ROW_HEIGHT);

  // Calculate totals
  const totalSquareFeet = units.reduce((sum, unit) => sum + (unit.square_feet || 0), 0);
  const totalCurrentRent = units.reduce((sum, unit) => sum + (unit.current_rent || 0), 0);
  const totalPfRent = rows.reduce((sum, row) => sum + (row.pf_rent || 0), 0);

  // Deprecated event wiring removed in favor of props-based totals

  const CustomFooter = () => (
    <div style={{ 
      padding: '14px 16px', 
      backgroundColor: '#ffffff', 
      borderTop: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: 14.5
    }}>
      {allowAddUnit ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addSingleUnit}>
            Add Unit
          </Button>
        </div>
      ) : (
        <div />
      )}
      <div style={{ display: 'flex', flex: 1, flexWrap: 'wrap', justifyContent: 'space-between', columnGap: 24, rowGap: 4, marginLeft: allowAddUnit ? 24 : 0, textAlign: 'right', color: '#1f2937', whiteSpace: 'nowrap' }}>
        <div style={{ textAlign: 'right' }}>
          <span style={{ fontWeight: 600 }}>Total Units:</span> {units.length}
        </div>
        {(() => {
          const grossSqFtField = modelDetails?.user_model_field_values.find((field: any) => field.field_key == "Gross Square Feet");
          const grossSqFtValue = grossSqFtField ? Number(grossSqFtField.value) : null;
          const isWarning = grossSqFtValue !== null && totalSquareFeet > grossSqFtValue;
          return (
            <>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontWeight: 600 }}>Total Rentable Square Feet:</span>{" "}
                <span style={isWarning ? { color: "#d32f2f", fontWeight: 600 } : {}}>
                  {totalSquareFeet.toLocaleString()}
                </span>
              </div>
              {grossSqFtField && (
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontWeight: 600 }}>Gross Square Feet:</span>{" "}
                  {grossSqFtField.value !== undefined && grossSqFtField.value !== null && !isNaN(Number(grossSqFtField.value))
                    ? Number(grossSqFtField.value).toLocaleString()
                    : "N/A"}
                </div>
              )}
            </>
          );
        })()}

        <div style={{ textAlign: 'right' }}>
          <span style={{ fontWeight: 600 }}>Total Current Rent:</span> ${totalCurrentRent.toLocaleString()}
        </div>
        {showMarketRentAssumptions && (
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 600 }}>Total Pro Forma Rent:</span> ${totalPfRent.toLocaleString()}
          </div>
        )}
      </div>
      {units.length > 100 && (
        <GridPagination />
      )}
    </div>
  );

  const handleOpenModal = () => setOpen(true);
  const handleCloseModal = () => setOpen(false);
  const handleOpenGrowthModal = () => setGrowthOpen(true);
  const handleCloseGrowthModal = () => setGrowthOpen(false);
  const handleOpenPfModal = () => setPfOpen(true);
  const handleClosePfModal = () => setPfOpen(false);

  const layoutExists = layoutNameTaken(marketRentAssumptions, newLayoutName);

  const growthRateExists = growthRates.some(
    (r) => r.name.trim().toLowerCase() === newGrowthRateName.trim().toLowerCase()
  );

  const isAddGrowthRateDisabled = !newGrowthRateName.trim() || growthRateExists;

  const isAddDisabled = !newLayoutName.trim() || layoutExists;

  // --- Floor-plan square feet ---------------------------------------------
  const parsedNewLayoutSqFt = parseSqFt(newLayoutSqFt);

  const suggestedLayoutName = layoutExists
    ? suggestLayoutName(newLayoutName, parsedNewLayoutSqFt)
    : '';

  const suggestionAvailable =
    !!suggestedLayoutName && !layoutNameTaken(marketRentAssumptions, suggestedLayoutName);

  // A unit inherits its plan's square feet only when it has none yet, or when
  // it still matches the plan's previous value. Anything else is a per-unit
  // override and is left alone.
  const isCascadeEligible = (unit: Unit, prevSqFt: number | null) => {
    const sf = Number(unit.square_feet ?? 0);
    if (!sf || sf <= 0) return true;
    return prevSqFt !== null && sf === prevSqFt;
  };

  const commitLayoutSqFt = (idx: number) => {
    const draft = layoutSqFtEdit[idx];
    setLayoutSqFtEdit((prev) => {
      const copy = { ...prev };
      delete copy[idx];
      return copy;
    });
    if (draft === undefined) return;

    const assumption = marketRentAssumptions[idx];
    if (!assumption) return;

    const nextSqFt = parseSqFt(draft);
    const prevSqFt = assumption.square_feet ?? null;
    if (nextSqFt === prevSqFt) return;

    setMarketRentAssumptions((prev) =>
      prev.map((a, i) => (i === idx ? { ...a, square_feet: nextSqFt } : a))
    );

    if (nextSqFt === null) return;
    const layoutUnits = units.filter((u) => u.layout === assumption.layout);
    const eligible = layoutUnits.filter((u) => isCascadeEligible(u, prevSqFt));
    if (eligible.length === 0) return;
    setSqFtCascade({
      layout: assumption.layout,
      nextSqFt,
      prevSqFt,
      count: eligible.length,
      skipped: layoutUnits.length - eligible.length,
    });
  };

  const applySqFtCascade = () => {
    if (!sqFtCascade) return;
    const { layout, nextSqFt, prevSqFt } = sqFtCascade;
    setUnits((prev) =>
      prev.map((u) =>
        u.layout === layout && isCascadeEligible(u, prevSqFt) ? { ...u, square_feet: nextSqFt } : u
      )
    );
    setSqFtCascade(null);
  };

  // --- Mobile (card) view -------------------------------------------------
  // On phones / small tablets the horizontally-scrolling grid is replaced by a
  // stacked card per unit, each opening a vertical edit form. Desktop is the
  // unchanged DataGrid above.
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [editUnitId, setEditUnitId] = useState<string | null>(null);
  const layoutOptions = marketRentAssumptions.map((a) => a.layout);
  const rentTypeOptions = growthRates.filter((r) => r.type === 'rental').map((r) => r.name);
  const editingUnit = editUnitId ? units.find((u) => u.id === editUnitId) ?? null : null;
  const editingIndex = editUnitId ? units.findIndex((u) => u.id === editUnitId) : -1;
  const vacateLabels = VACATE_OPTIONS.map((option) => option.label);

  const renderUnitField = (label: string, value: React.ReactNode) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: '#6b7280', fontSize: '0.62rem', display: 'block' }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Box>
  );

  const mobileUnitCards = (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {rows.map((row) => (
          <Box
            key={row.id}
            sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, bgcolor: '#ffffff' }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>Unit {row.unitNumber}</Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.layout}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                <Tooltip title="Edit">
                  <IconButton size="small" color="primary" onClick={() => setEditUnitId(row.id)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Duplicate">
                  <IconButton size="small" onClick={() => handleDuplicateRow(row.id)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <span>
                    <IconButton size="small" color="error" disabled={units.length === 1} onClick={() => handleDeleteRow(row.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.5 }}>
              {renderUnitField('Square Feet', Number(row.square_feet || 0).toLocaleString())}
              {showMarketRentAssumptions
                ? renderUnitField('Pro Forma Rent', `$${Number(row.pf_rent || 0).toLocaleString()}/mo`)
                : renderUnitField('Current Rent', `$${Number(row.current_rent || 0).toLocaleString()}/mo`)}
              {renderUnitField('Rent Type', row.rent_type)}
              {activeStepTitle === 'Residential Rental Units' &&
                renderUnitField('Growth %', `${Number(row.growth_rate || 0).toLocaleString()}%`)}
              {vacate && renderUnitField('Rent Change', vacateLabels[Number(row.vacate_flag) || 0])}
              {vacate && renderUnitField('Vacate Month', `Month ${Number(row.vacate_month) || 0}`)}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Add unit + totals */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        {allowAddUnit && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addSingleUnit}>
            Add Unit
          </Button>
        )}
        <Box sx={{ textAlign: 'right', fontSize: 13, color: '#1f2937', ml: 'auto' }}>
          <div><span style={{ fontWeight: 600 }}>Total Units:</span> {units.length}</div>
          <div><span style={{ fontWeight: 600 }}>Total Sq Ft:</span> {totalSquareFeet.toLocaleString()}</div>
          {showMarketRentAssumptions ? (
            <div><span style={{ fontWeight: 600 }}>Total Pro Forma Rent:</span> ${totalPfRent.toLocaleString()}</div>
          ) : (
            <div><span style={{ fontWeight: 600 }}>Total Current Rent:</span> ${totalCurrentRent.toLocaleString()}</div>
          )}
        </Box>
      </Box>
    </Box>
  );

  const mobileEditDialog = (
    <Dialog open={!!editingUnit} onClose={() => setEditUnitId(null)} fullWidth maxWidth="xs">
      <DialogTitle>Edit Unit {editingIndex >= 0 ? editingIndex + 1 : ''}</DialogTitle>
      <DialogContent>
        {editingUnit && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select label="Layout" size="small" fullWidth value={editingUnit.layout}
              onChange={(e) => handleCellChange(editingUnit.id, 'layout', e.target.value)}
            >
              {layoutOptions.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </TextField>

            <MobileNumberField
              label="Square Feet" type="number" size="small" fullWidth value={editingUnit.square_feet ?? ''}
              onChange={(e) => handleCellChange(editingUnit.id, 'square_feet', Math.max(0, Number(e.target.value)))}
            />

            {!showMarketRentAssumptions ? (
              <MobileNumberField
                label="Current Rent" type="number" size="small" fullWidth value={editingUnit.current_rent ?? ''}
                onChange={(e) => handleCellChange(editingUnit.id, 'current_rent', Math.max(0, Number(e.target.value)))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            ) : (
              <TextField
                label="Pro Forma Rent (from layout)" size="small" fullWidth disabled
                value={`$ ${Number(marketRentAssumptions.find((a) => a.layout === editingUnit.layout)?.pf_rent ?? 0).toLocaleString()} / month`}
              />
            )}

            {!showMarketRentAssumptions ? (
              <TextField
                select label="Rent Type" size="small" fullWidth value={editingUnit.rent_type}
                onChange={(e) => handleCellChange(editingUnit.id, 'rent_type', e.target.value)}
              >
                {rentTypeOptions.map((rt) => <MenuItem key={rt} value={rt}>{rt}</MenuItem>)}
              </TextField>
            ) : (
              <TextField label="Rent Type" size="small" fullWidth disabled value={editingUnit.rent_type} />
            )}

            {vacate && (
              <>
                <TextField
                  select label="Rent Change Behavior" size="small" fullWidth value={editingUnit.vacate_flag ?? 0}
                  onChange={(e) => handleCellChange(editingUnit.id, 'vacate_flag', parseInt(e.target.value, 10))}
                >
                  <MenuItem value={0}>Keep Tenant</MenuItem>
                  <MenuItem value={1}>Vacate &amp; Re-Lease</MenuItem>
                  <MenuItem value={2}>Market Adjustment</MenuItem>
                </TextField>
                <MobileNumberField
                  label="Vacate Month" type="number" size="small" fullWidth value={editingUnit.vacate_month ?? 0}
                  onChange={(e) => handleCellChange(editingUnit.id, 'vacate_month', Math.max(0, Math.round(Number(e.target.value))))}
                />
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditUnitId(null)} variant="contained">Done</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <div style={{padding: 20}}>


        {/* <div style={{ marginBottom: 10, padding: 20, paddingBottom: 10 }}>
          {showMarketRentLayouts && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <label>
                  Add units:
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={unitCount}
                    onChange={(e) => {
                      let newCount = parseInt(e.target.value, 10) || 1;
                      if (newCount < 1) newCount = 1;
                      if (newCount > 100) newCount = 100;
                      setUnitCount(newCount);
                    }}
                    style={{ marginLeft: '10px' }}
                  />
                </label>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => updateUnits(unitCount)}
                  sx={{ marginLeft: '10px' }}
                >
                  Add {unitCount} {unitCount > 1 ? 'Units' : 'Unit'}
                </Button>
              </div>
              <div>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ marginRight: '10px' }}
                  onClick={handleOpenGrowthModal}
                >
                  Edit Rent Types & Growth Rates
                </Button>
                
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleOpenModal}
                >
                  Edit Layouts
                </Button>
              </div>
            </div>
          )}

{growthRatesOnly && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
               
              </div>
              <div>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ marginRight: '10px' }}
                  onClick={handleOpenGrowthModal}
                >
                  Edit Rent Types & Growth Rates
                </Button>
                
                
              </div>
            </div>
          )}




          
        </div>
     */}
          {isMobile ? mobileUnitCards : (
          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns.map((column) => ({ ...column, sortable: column.field !== 'delete' }))}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              disableRowSelectionOnClick
              processRowUpdate={processRowUpdate}
              slots={{
                footer: CustomFooter
              }}
              rowHeight={ROW_HEIGHT}

              getRowClassName={getRowClassName}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[100]}
              columnVisibilityModel={{
                vacate_flag: vacate,
                vacate_month: vacate,
              }}
              hideFooterSelectedRowCount
              disableColumnMenu
              disableColumnFilter
              disableColumnSelector
              sx={{
                height: gridHeight,
                minWidth: gridMinWidth,
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                
                '& .MuiDataGrid-main': {
                  background: '#ffffff',
                },
                '& .MuiDataGrid-columnHeaders': {
                  background: '#ffffff',
                  minHeight: 52,
                  maxHeight: 52,
                  borderBottom: '1px solid #e5e7eb',
                  minWidth: gridMinWidth,
                },
                '& .MuiDataGrid-columnHeader': {
                  background: '#ffffff',
                  minHeight: 52,
                  maxHeight: 52,
                },
                '& .MuiDataGrid-columnHeaderTitleContainer': {
                  background: '#ffffff',
                },
                // Standardize header font across all columns
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 600,
                  fontSize: 14.5,
                  fontFamily: 'inherit',
                  textTransform: 'none',
                  lineHeight: '52px',
                  color: '#1f2937',
                },
                // Cleaner grid appearance
                // '& .MuiDataGrid-columnSeparator': { display: 'none' },
                '& .MuiDataGrid-cell': { 
                  borderBottom: '1px solid rgba(15, 23, 42, 0.08)', 
                  background: '#ffffff',
                  fontSize: 14.5,
                  color: '#1f2937',
                },
                '& .MuiDataGrid-cell.u-noneditable-cell': { color: '#64748b' },
                '& .MuiDataGrid-cell:not(.u-editable-cell):not(.u-noneditable-cell)': { color: '#6b7280' },
                '& .u-editable-input': {
                  border: 'none',
                  borderBottom: '1px solid transparent',
                  borderRadius: 0,
                  background: 'transparent',
                },
                '& .MuiDataGrid-row:hover .u-editable-input, & .u-editable-input:focus': {
                  borderBottom: '2px solid #4f8bd6 !important',
                },
                '& .MuiDataGrid-row': { background: '#ffffff' },
                '& .u-row-odd': { background: '#ffffff' },
                '& .u-row-even': { background: '#f9fafb' },
                '& .MuiDataGrid-row:hover': { backgroundColor: '#f3f6fb' },
                // Row action visibility only on hover/focus
                '& .u-row-action': {
                  opacity: 0,
                  visibility: 'hidden',
                  pointerEvents: 'none',
                  transition: 'opacity 120ms ease'
                },
                '& .MuiDataGrid-row:hover .u-row-action, & .MuiDataGrid-cell:focus-within .u-row-action': {
                  opacity: 1,
                  visibility: 'visible',
                  pointerEvents: 'auto'
                },
                // Custom caret via element: hidden by default, shown on row hover or focus
                '& .u-caret': { opacity: 0, transition: 'opacity 120ms ease', color: colors.blue, fontSize: '1.25rem' },
                '& .u-select:focus + .u-caret': { opacity: 1 },
                '& .MuiDataGrid-row:hover .u-caret': { opacity: 1 },
                '& .MuiDataGrid-cell:hover .u-caret': { opacity: 1 },
                '& .MuiDataGrid-virtualScroller': {
                  background: '#ffffff',
                  overflowX: 'auto',
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                  minWidth: gridMinWidth,
                },
                '& .MuiDataGrid-columnHeadersInner': {
                  minWidth: gridMinWidth,
                },
                '& .MuiDataGrid-footerContainer': {
                  background: '#ffffff',
                  borderTop: '1px solid #e5e7eb',
                },
                '& .u-select': {
                  color: '#1f2937',
                  fontSize: 14.5,
                  fontFamily: 'inherit',
                  fontWeight: 500,
                },
                '& .u-unit': {
                  color: '#6b7280',
                  fontWeight: 500,
                },
                '& .u-value': {
                  color: '#111827',
                  fontWeight: 600,
                },
                '& .u-muted': {
                  color: '#9aa3b2',
                  fontWeight: 400,
                },
                '& .u-muted .u-select': {
                  color: '#9aa3b2',
                  fontWeight: 400,
                },
                // Tablets and small laptops: slightly tighter text and padding so
                // every column and the footer totals fit without sideways scroll.
                '@media (max-width: 1400px)': {
                  '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': { paddingLeft: '6px', paddingRight: '6px' },
                  '& .MuiDataGrid-cell': { fontSize: 13.5 },
                  // The vacate select sets its font inline, so this has to win over it.
                  '& .u-select': { fontSize: '13.5px !important' },
                  '& .MuiDataGrid-columnHeader .MuiTypography-root, & .MuiDataGrid-columnHeaderTitle': { fontSize: 13.5 },
                },
              }}
            />
          </Box>
          )}

          {mobileEditDialog}

      </div>




      <Dialog open={growthOpen} onClose={handleCloseGrowthModal} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Rent Type</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 160px 72px',
                gap: 1,
                px: 1,
                py: 0.5,
                mb: 1,
                borderBottom: '1px solid #e0e0e0'
              }}
            >
              <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>Name</Typography>
              <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Growth Rate %</Typography>
              <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary', textAlign: 'center' }}>Remove</Typography>
            </Box>
            {growthRates.filter(rate => rate.type === 'rental').map((rate, idx) => (
              <Box
                key={`${rate.type}:${rate.name}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 160px 72px',
                  gap: 1,
                  alignItems: 'center',
                  px: 1.5,
                  py: 1,
                  minHeight: 44,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  mb: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={nameEdit[idx] !== undefined ? nameEdit[idx] : rate.name}
                    onChange={(e) => {
                      const v = e.target.value;
                      setNameEdit((prev) => ({ ...prev, [idx]: v }));
                      setNameError((prev) => ({ ...prev, [idx]: '' }));
                    }}
                    variant="standard"
                    size="small"
                    placeholder="Untitled"
                    sx={{ pl: 1, pr: 1, minWidth: 0, flex: 1 }}
                    inputProps={{ maxLength: 100, readOnly: nameEdit[idx] === undefined }}
                    error={Boolean(nameError[idx])}
                    helperText={nameError[idx] || undefined}
                    FormHelperTextProps={{ sx: { m: 0 } }}
                  />
                  {nameEdit[idx] === undefined ? (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setNameEdit((prev) => ({ ...prev, [idx]: rate.name }));
                        setNameError((prev) => ({ ...prev, [idx]: '' }));
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        const raw = nameEdit[idx] ?? '';
                        const newName = raw.trim();
                        if (!newName) {
                          setNameError((prev) => ({ ...prev, [idx]: 'Name required' }));
                          return;
                        }
                        const oldName = rate.name;
                        if (isDuplicateRentalName(growthRates, oldName, newName)) {
                          setNameError((prev) => ({ ...prev, [idx]: 'Duplicate name' }));
                          return;
                        }
                        setGrowthRates(renameRentalGrowthRate(growthRates, oldName, newName));
                        setUnits((prev) => prev.map((u) => (u.rent_type === oldName ? { ...u, rent_type: newName } : u)));
                        setNameEdit((prev) => {
                          const copy = { ...prev };
                          delete copy[idx];
                          return copy;
                        });
                        setNameError((prev) => {
                          const copy = { ...prev };
                          delete copy[idx];
                          return copy;
                        });
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <NumberInput
                  value={rate.value}
                  onChange={(val) => {
                    const parsed = val === '' ? 0 : Number(val);
                    const newGrowthRates = growthRates.map((r) =>
                      r.type === 'rental' && r.name === rate.name
                        ? { ...r, value: parsed }
                        : r
                    );
                    setGrowthRates(newGrowthRates);
                  }}
                  size="small"
                  variant="standard"
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': { backgroundColor: 'transparent' }
                  }}
                  endAdornment={<InputAdornment position="end">%</InputAdornment>}
                  InputProps={{ sx: { textAlign: 'right' } }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Tooltip
                    title={units.some(unit => unit.rent_type === rate.name) ? "Cannot delete: used by a unit" : "Delete"}
                    disableHoverListener={!units.some(unit => unit.rent_type === rate.name)}
                    disableFocusListener={!units.some(unit => unit.rent_type === rate.name)}
                    disableTouchListener={!units.some(unit => unit.rent_type === rate.name)}
                    arrow
                  >
                    <span>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => {
                          const removeIdx = growthRates.findIndex((r) => r.type === 'rental' && r.name === rate.name);
                          const newGrowthRates = growthRates.filter((_, i) => i !== removeIdx);
                          setGrowthRates(newGrowthRates);
                        }}
                        disabled={units.some(unit => unit.rent_type === rate.name)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, width: '100%' }}>
              <TextField
                label="New Growth Rate Name"
                value={newGrowthRateName}
                onChange={(e) => setNewGrowthRateName(e.target.value)}
                size="small"
                sx={{ mr: 2, flex: 1 }}
                error={growthRateExists}
                helperText={growthRateExists ? "Growth rate name already exists" : ""}
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={() => {
                  setGrowthRates([
                    ...growthRates,
                    { name: newGrowthRateName.trim(), value: 0, type: 'rental' }
                  ]);
                  setNewGrowthRateName('');
                }}
                disabled={isAddGrowthRateDisabled}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Add Growth Rate
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={handleCloseGrowthModal} variant="contained">Done</Button>
          </Box>
        </DialogContent>
      </Dialog>

    

      <Dialog open={open} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Market Rent Assumptions</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {marketRentAssumptions.map((assumption, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  px: 1,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  minHeight: 44,
                  fontSize: 15
                }}
              >
                <Box sx={{ flex: 1, fontWeight: 500, color: 'text.primary', pl: 1, minWidth: 0 }}>
                  {assumption.layout || <span style={{ color: '#aaa' }}>Untitled</span>}
                </Box>
                <NumberInput
                  value={layoutSqFtEdit[idx] !== undefined ? layoutSqFtEdit[idx] : (assumption.square_feet ?? '')}
                  onChange={(val) => setLayoutSqFtEdit((prev) => ({ ...prev, [idx]: String(val) }))}
                  placeholder="—"
                  size="small"
                  variant="standard"
                  endAdornment={<InputAdornment position="end">sf</InputAdornment>}
                  inputProps={{
                    onBlur: () => commitLayoutSqFt(idx),
                    'aria-label': `Square feet for ${assumption.layout}`
                  }}
                  sx={{ width: 120, flexShrink: 0, '& .MuiInputBase-root': { bgcolor: 'transparent' } }}
                />
                {marketRentAssumptions.length > 1 && (
                  <Button
                    color="error"
                    size="small"
                    variant="text"
                    disabled={units.some(unit => unit.layout === assumption.layout)}
                    onClick={() => {
                      const newAssumptions = marketRentAssumptions.filter((_, i) => i !== idx);
                      setMarketRentAssumptions(newAssumptions);
                    }}
                    sx={{
                      minWidth: 0,
                      px: 1,
                      ml: 1,
                      fontSize: 13
                    }}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 2 }}>
              <TextField
                label="New Layout Name"
                value={newLayoutName}
                onChange={(e) => setNewLayoutName(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 0 }}
                error={layoutExists}
                helperText={layoutExists && !suggestionAvailable ? "Layout name already exists" : ""}
              />
              <NumberInput
                label="Sq Ft"
                value={newLayoutSqFt}
                onChange={(val) => setNewLayoutSqFt(String(val))}
                size="small"
                variant="outlined"
                endAdornment={<InputAdornment position="end">sf</InputAdornment>}
                sx={{ width: 130, flexShrink: 0 }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  setMarketRentAssumptions([
                    ...marketRentAssumptions,
                    { layout: newLayoutName.trim(), pf_rent: 0, square_feet: parsedNewLayoutSqFt }
                  ]);
                  setNewLayoutName('');
                  setNewLayoutSqFt('');
                }}
                disabled={isAddDisabled}
                sx={{ mt: 0.5, flexShrink: 0 }}
              >
                Add Layout
              </Button>
            </Box>
            {layoutExists && suggestionAvailable && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1, pl: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  "{newLayoutName.trim()}" already exists. Add it as "{suggestedLayoutName}" instead?
                </Typography>
                <Button size="small" onClick={() => setNewLayoutName(suggestedLayoutName)}>
                  Use this name
                </Button>
              </Box>
            )}
            {layoutExists && !suggestionAvailable && !parsedNewLayoutSqFt && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, pl: 0.5, color: 'text.secondary' }}>
                Enter square feet to add a same-named plan at a different size.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={handleCloseModal} variant="contained">Done</Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={pfOpen}
        onClose={handleClosePfModal}
        disablePortal
        container={() => document.getElementById('create-model-content')}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: 'calc(100% - 16px)', md: 960 },
            mx: { xs: 1, md: 'auto' }
          }
        }}
      >
        <DialogTitle>Let's determine market rents for the residential units</DialogTitle>
        <DialogContent>
          {isMobile ? (
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {marketRentAssumptions
                .filter(assumption => units.some(unit => unit.layout === assumption.layout))
                .map((assumption, idx) => {
                  const layoutUnitsAll = units.filter((u) => u.layout === assumption.layout);
                  const layoutUnits = layoutUnitsAll.filter((u) => {
                    const vf = Number(u?.vacate_flag ?? 0);
                    const vm = Number(u?.vacate_month ?? 0);
                    if (vf === 0) return true;
                    if ((vf === 1 || vf === 2) && vm > 0) return true;
                    return false;
                  });
                  const payingUnits = layoutUnits.filter((u) => Number(u?.current_rent ?? 0) > 0);
                  const numPayingUnits = payingUnits.length;
                  const totalPayingSqFt = payingUnits.reduce((s, u) => s + (Number(u.square_feet) || 0), 0);
                  const totalCurrentRentL = payingUnits.reduce((s, u) => s + (Number(u.current_rent) || 0), 0);
                  const avgCurrentRentAtLayout = numPayingUnits > 0 ? (totalCurrentRentL / numPayingUnits) : 0;
                  const avgCurrentRentPerSf = totalPayingSqFt > 0 ? (totalCurrentRentL / totalPayingSqFt) : 0;
                  const numUnitsAtLayout = layoutUnits.length;
                  const totalSqFtAtLayout = layoutUnits.reduce((s, u) => s + (Number(u.square_feet) || 0), 0);
                  const proFormaRentPerSf = totalSqFtAtLayout > 0 ? ((numUnitsAtLayout * (assumption.pf_rent || 0)) / totalSqFtAtLayout) : 0;
                  return (
                    <Box key={`${assumption.layout}-${idx}`} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, bgcolor: '#fff' }}>
                      <Typography sx={{ fontWeight: 700, color: '#1f2937', mb: 1.5 }}>{assumption.layout}</Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 2 }}>
                        {renderUnitField('Avg Current Rent', `$${avgCurrentRentAtLayout.toLocaleString(undefined, { maximumFractionDigits: 0 })}`)}
                        {renderUnitField('Avg Current Rent / SF', `$${avgCurrentRentPerSf.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)}
                        {renderUnitField('Pro Forma Rent / SF', `$${proFormaRentPerSf.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)}
                      </Box>
                      <Typography sx={{ color: '#6b7280', fontSize: '0.62rem', mb: 0.5 }}>
                        Market Rent
                      </Typography>
                      <NumberInput
                        variant="outlined"
                        size="small"
                        value={assumption.pf_rent}
                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                        endAdornment={<InputAdornment position="end">/ month</InputAdornment>}
                        onChange={(val) => {
                          const num = val === '' ? 0 : Number(val);
                          const newAssumptions = marketRentAssumptions.map((a) =>
                            a.layout === assumption.layout ? { ...a, pf_rent: num } : a
                          );
                          setMarketRentAssumptions(newAssumptions);
                        }}
                        sx={{ '& .MuiInputBase-root': { bgcolor: 'white' } }}
                        fullWidth
                      />
                    </Box>
                  );
                })}
            </Box>
          ) : (
          <Box sx={{ mt: 1, overflowX: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '0.8fr 1fr 1.2fr 1fr 1.4fr' },
                gap: 1,
                px: 1,
                py: 0.5,
                mb: 1,
                borderBottom: '1px solid #e0e0e0',
                minWidth: { xs: 640, sm: 'auto' }
              }}
            >
              <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>Layout</Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>Avg Current Rent</Typography>
                <Tooltip
                  title={"Averages of current rent exclude vacant or non-paying units; Averages are calculated only on currently paying units"}
                  arrow
                >
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    <InfoOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>Avg Current Rent / SF</Typography>
                <Tooltip
                  title={"Averages of current rent / sf exclude vacant or non-paying units; Averages are calculated only on currently paying units"}
                  arrow
                >
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    <InfoOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Pro Forma Rent / SF</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>Market Rents</Typography>
                <Tooltip
                  title={"If you have units vacating in the next step, the new rents will be reset to the market rents in the model. The market rents are also how you will show “Gross Potential Rent” in the financials."}
                  arrow
                >
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    <InfoOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {marketRentAssumptions
              .filter(assumption => units.some(unit => unit.layout === assumption.layout))
              .map((assumption, idx) => (
               <Box
                key={`${assumption.layout}-${idx}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '0.8fr 1fr 1.2fr 1fr 1.4fr' },
                  gap: 1.5,
                  alignItems: 'center',
                  mb: 1.2,
                  px: 1.5,
                  py: 1,
                  backgroundColor: 'grey.100',
                  borderRadius: 1,
                  minWidth: { xs: 640, sm: 'auto' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 500, color: '#444' }}>{assumption.layout}</Typography>
                </Box>

                {(() => {
                  const layoutUnitsAll = units.filter((u) => u.layout === assumption.layout);
                  // Pro-forma set: units that will pay rent at some point (vacate_flag=0, or vacating with a scheduled month)
                  const layoutUnits = layoutUnitsAll.filter((u) => {
                    const vf = Number(u?.vacate_flag ?? 0);
                    const vm = Number(u?.vacate_month ?? 0);
                    if (vf === 0) return true;
                    if ((vf === 1 || vf === 2) && vm > 0) return true;
                    return false;
                  });
                  // Current-rent averages reflect only units currently paying rent (tooltip: "exclude vacant or non-paying units")
                  const payingUnits = layoutUnits.filter((u) => Number(u?.current_rent ?? 0) > 0);
                  const numPayingUnits = payingUnits.length;
                  const totalPayingSqFt = payingUnits.reduce((sum, u) => sum + (Number(u.square_feet) || 0), 0);
                  const totalCurrentRent = payingUnits.reduce((sum, u) => sum + (Number(u.current_rent) || 0), 0);
                  const avgCurrentRentAtLayout = numPayingUnits > 0 ? (totalCurrentRent / numPayingUnits) : 0;
                  const avgCurrentRentPerSf = totalPayingSqFt > 0 ? (totalCurrentRent / totalPayingSqFt) : 0;
                  // Pro-forma per-SF still spans the full stabilized set
                  const numUnitsAtLayout = layoutUnits.length;
                  const totalSqFtAtLayout = layoutUnits.reduce((sum, u) => sum + (Number(u.square_feet) || 0), 0);
                  const proFormaRentPerSf = totalSqFtAtLayout > 0 ? ((numUnitsAtLayout * (assumption.pf_rent || 0)) / totalSqFtAtLayout) : 0;
                  return (
                    <>
                      <Typography sx={{ fontWeight: 600, color: '#222', textAlign: 'right' }}>
                        ${avgCurrentRentAtLayout.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </Typography>
                      <Typography sx={{ fontWeight: 600, color: '#222', textAlign: 'right' }}>
                        ${avgCurrentRentPerSf.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                      <Typography sx={{ fontWeight: 600, color: '#222', textAlign: 'right' }}>
                        ${proFormaRentPerSf.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </>
                  );
                })()}

                <NumberInput
                  variant="outlined"
                  size="small"
                  value={assumption.pf_rent}
                  startAdornment={<InputAdornment position="start">$</InputAdornment>}
                  endAdornment={<InputAdornment position="end">/ month</InputAdornment>}
                  onChange={(val) => {
                    const num = val === '' ? 0 : Number(val);
                    const newAssumptions = marketRentAssumptions.map((a) =>
                      a.layout === assumption.layout ? { ...a, pf_rent: num } : a
                    );
                    setMarketRentAssumptions(newAssumptions);
                  }}
                  sx={{
                    '& .MuiInputBase-root': { bgcolor: 'white' },
                  }}
                  fullWidth
                  InputProps={{ sx: { textAlign: 'right' } }}
                />
              </Box>
            ))}
          </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={handleClosePfModal} variant="contained">Done</Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={setAllMenu?.anchor ?? null}
        open={!!setAllMenu}
        onClose={() => setSetAllMenu(null)}
      >
        {setAllMenu
          ? setAllOptions(setAllMenu.field).map((option) => {
              const field = setAllMenu.field;
              return (
                <MenuItem
                  key={String(option.value)}
                  onClick={() => {
                    setSetAllConfirm({ field, value: option.value, label: option.label });
                    setSetAllMenu(null);
                  }}
                >
                  {option.label}
                </MenuItem>
              );
            })
          : null}
      </Menu>

      <Dialog open={!!setAllConfirm} onClose={() => setSetAllConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Apply to all units?</DialogTitle>
        <DialogContent>
          <Typography>
            Set all {units.length.toLocaleString()} unit{units.length === 1 ? '' : 's'} to{' '}
            {setAllConfirm?.label}?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: '#6b7280' }}>
            You can still change individual units afterwards.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetAllConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={applySetAll}>Set all</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!sqFtCascade} onClose={() => setSqFtCascade(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Update units with this layout?</DialogTitle>
        <DialogContent>
          {sqFtCascade && (
            <>
              <Typography sx={{ mb: 1 }}>
                Update square feet on {sqFtCascade.count} unit{sqFtCascade.count === 1 ? '' : 's'} with
                the "{sqFtCascade.layout}" layout to {sqFtCascade.nextSqFt.toLocaleString()} sf?
              </Typography>
              {sqFtCascade.skipped > 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {sqFtCascade.skipped} unit{sqFtCascade.skipped === 1 ? ' has its' : 's have their'} own
                  square feet and will not be changed.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSqFtCascade(null)}>Keep unit values</Button>
          <Button variant="contained" onClick={applySqFtCascade}>Update units</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UnitTable;
